/**
 * What a poster id actually is, in words — the board's half of it.
 *
 * A contract strip reading `683DDD2C24B5A2E114AF15FA · 1,601,000 EXPECTED` says
 * nothing a member can check against the game, and the country that posted a
 * contract is exactly the fact they use to judge it. The panel resolves the
 * same id through `extension-mu/battle.js`, from the same endpoint, so both
 * surfaces print the same word.
 *
 * Best-effort: a name that will not resolve leaves the id where it was, because
 * the id is what the product runs on. Names are cached in localStorage, since a
 * country's name does not change.
 */
const API = "https://api2.warera.io/trpc/";
const STORE_KEY = "muCountryNames";

const pending = new Map<string, Promise<string | null>>();

function readStore(): Record<string, string> {
  try {
    const raw = localStorage.getItem(STORE_KEY);
    const parsed: unknown = raw ? JSON.parse(raw) : {};
    return parsed && typeof parsed === "object" ? (parsed as Record<string, string>) : {};
  } catch {
    return {};
  }
}

function remember(countryId: string, name: string) {
  try {
    const store = readStore();
    store[countryId] = name;
    localStorage.setItem(STORE_KEY, JSON.stringify(store));
  } catch {
    // A private window or a full store costs one request next time, no more.
  }
}

/** The country's own name, or null when the game will not say. */
export async function countryName(countryId: string): Promise<string | null> {
  if (!countryId || !/^[0-9a-f]{24}$/i.test(countryId)) return null;
  const held = pending.get(countryId);
  if (held) return held;

  const job = (async () => {
    const store = readStore();
    if (typeof store[countryId] === "string") return store[countryId];
    const res = await fetch(
      `${API}country.getCountryById?input=${encodeURIComponent(JSON.stringify({ countryId }))}`,
    );
    if (!res.ok) return null;
    const body = (await res.json()) as { result?: { data?: { name?: string; code?: string } } };
    const found = body?.result?.data;
    const label = found?.name ?? (found?.code ? String(found.code).toUpperCase() : null);
    if (label) remember(countryId, label);
    return label ?? null;
  })().catch(() => null);

  pending.set(countryId, job);
  const resolved = await job;
  if (resolved === null) pending.delete(countryId);
  return resolved;
}
